import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0404 from "../lesson-04/lesson";

const linkLesson0404 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0404.id);

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>When choosing a transducer, it is important to make an informed decision. Technical data sheets give you access to various parameters that describe the performance of the considered device. In this Lesson, the linear characteristics of transducers [1, 2, 3] will be presented.
<h3 class=sectionHead>Sensitivity</h3>
<p>Most audio systems are designed to have a predominantly linear behaviour, at least in their operating range. To characterize this behaviour, several parameters have been defined. One of the most used is the sensitivity. It represents the ratio of the output quantity divided by the input quantity and is given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>H</mi>
            <mo class=MathClass-open>(</mo>
            <mi>f</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>Y</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>f</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>X</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>f</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>X</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Y</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are the spectra of the input and output signals. Note that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>H</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is generally a complex function giving the amplitude and the phase of the output signal relative to the input signal.
<p>For instance, the voltage sensitivity of a microphone is given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>U</mi>
   </math>
   is the output voltage while
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
   </math>
   is the pressure applied to the membrane of the microphone. This ratio is usually calculated using the RMS values or maximum values of the input and output signals for each frequency. The unit of sensitivity is V/Pa but it is often expressed in mV/Pa because microphones are usually not very efficient. Sensitivity can also be expressed in dB
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mstyle class=text>
                     <mtext>dB</mtext>
                  </mstyle>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mn>0</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>μ</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mstyle class=text>
                           <mtext>re</mtext>
                        </mstyle>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mstyle class=text>
               <mtext>re</mtext>
            </mstyle>
         </mrow>
      </msub>
   </math>
   is the reference sensitivity. Unfortunately,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mstyle class=text>
               <mtext>re</mtext>
            </mstyle>
         </mrow>
      </msub>
   </math>
   is not standardized, some manufactures use
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
    V/Pa while others use
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
    V/
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>μ</mi>
   </math>
   Bar. In order to precisely specify a sensitivity, it is necessary to describe the measurement conditions or refer to a standard.<br class=newline>For example:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>μ</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mn>5</mn>
   </math>
    mV/Pa at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>0</mn>
      <mn>0</mn>
      <mn>0</mn>
   </math>
    Hz, open circuit on acoustic calibrator. This sensitivity is also equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
            <mi>B</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mn>3</mn>
      <mn>6</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>
    dB re
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
    V/Pa.<br class=newline>For other types of sensors, the sensitivity is obtained in the same way (ratio between the output signal and the input signal) giving units such as V
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>⋅</mo>
   </math>
   s/m for velocity sensors or geophones or V
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>⋅</mo>
   </math>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
        <mn>s</mn>
        <mn>2</mn>
      </msup>
   </math>
   /m for accelerometers.<br class=newline>
<p>
   For actuators, the sensitivity can also be calculated using the output quantity (restored force, radiated pressure) obtained when the device is excited by a given voltage. For instance, the sensitivity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   of a sound source radiating a pressure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
   </math>
   when excited by a voltage
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>U</mi>
   </math>
   is given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>U</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   and is expressed in Pa/V. However, the standard committee for speaker measurements chose to define the sensitivity as the pressure level (in dB SPL, re.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>5</mn>
         </mrow>
      </msup>
   </math>
    Pa, see <a href=${linkLesson0404} target="_blank">Lesson 4</a>), radiated at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
    m on axis in free field conditions by the tested source when driven by a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=bold-script>𝒫</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
    W electrical signal. For a speaker with a nominal impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>N</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>8</mn>
   </math>

   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Ω</mi>
   </math>, the driving voltage
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>U</mi>
   </math>
   will be equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>U</mi>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mi mathvariant=bold-script>𝒫</mi>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </msub>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mn>8</mn>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>≃</mo>
      <mn>2</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>8</mn>
      <mn>3</mn>
   </math>
    V. Note that an input signal of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
    W does not exactly corresponds to an input signal of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>8</mn>
      <mn>3</mn>
   </math>
    V as the electrical impedance of sound source is complex and frequency dependent. Hifi and consumer loudspeakers generally have a sensitivity of about 85 to 95 dB.
<h3 class=sectionHead>Frequency response of transducers</h3>
<p>
   Most transducers have a sensitivity that depends on frequency. Their frequency response represents the spectrum of the output signal divided by the spectrum of the input signal (see Eq. 1). The frequency response contains amplitude and phase information. An example for a microphone is plotted in <a href=# data-img-src=${config.ASSETS_URL}/04/07/theory/figure-1.svg data-img-alt="Figure 1. Example of a microphone frequency response.">Figure 1</a>. However, many manufacturers only provide the amplitude curve without specifying the phase response.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/07/theory/figure-1.svg alt="Figure 1. Example of a microphone frequency response."><figcaption class=caption>Figure 1. Example of a microphone frequency response.</figcaption></figure>
<p>
   The nominal sensitivity, defined for a specific frequency (for instance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mn>5</mn>
      <mn>0</mn>
   </math>
    Hz or
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>0</mn>
      <mn>0</mn>
      <mn>0</mn>
   </math>
    Hz are standardized values) can easily be obtained from the frequency response. An example is highlighted in the amplitude plot of <a href=# data-img-src=${config.ASSETS_URL}/04/07/theory/figure-1.svg data-img-alt="Figure 1. Example of a microphone frequency response.">Figure 1</a>, the sensitivity at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>0</mn>
      <mn>0</mn>
      <mn>0</mn>
   </math>
    Hz is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
            <mi>B</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mn>5</mn>
      <mn>5</mn>
   </math>
    dB (re
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
    V/Pa), yielding to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>μ</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc>.</mo>
      <mrow>
        <mi>10</mi>
      </mrow>
   </math>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mfrac>
        <mrow>
          <msub>
            <mrow>
              <mi>S</mi>
            </mrow>
            <mrow>
              <mi>dB</mi>
            </mrow>
          </msub>
        </mrow>
        <mrow>
          <mn>20</mn>
        </mrow>
      </mfrac>
   </math>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mo>=</mo>
   <mn>18</mn>
   </math> mV/Pa.
   <br />
   The frequency range of a transducer is the interval between lower and upper cut-off frequencies. These frequency limits are defined by setting a tolerance with respect to the nominal response. As requirements depend on the application, tolerance values can take different values. For example, the upper and lower cut-off frequencies of a bandpass filter are the values at which the amplitude has dropped by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
   </math>
    dB from the nominal response (corresponding to a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>7</mn>
      <mn>0</mn>
      <mn>7</mn>
   </math>
   amplitude ratio). For measurement microphones, a tolerance of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>±</mo>
      <mn>1</mn>
   </math>
    dB can be used, meaning that no emphasis or attenuation of more than
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
    dB will occur in this frequency band. An example is given in <a href=# data-img-src=${config.ASSETS_URL}/04/07/theory/figure-1.svg data-img-alt="Figure 1. Example of a microphone frequency response.">Figure 1</a> for a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>±</mo>
      <mn>3</mn>
   </math>
    dB tolerance relative to the nominal sensitivity at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
    kHz. In that case, the lower cut-off frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>
   is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
      <mn>0</mn>
   </math>
    Hz while the higher cut-off frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>h</mi>
         </mrow>
      </msub>
   </math>
   is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>3</mn>
   </math>
    kHz. Between these two values, the microphone sensitivity is always within
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
   </math>
    dB of the nominal sensitivity. On a datasheet, this information would be written: Frequency response
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>±</mo>
      <mn>3</mn>
   </math>
    dB:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
      <mn>0</mn>
   </math>
    Hz to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>3</mn>
   </math>
    kHz.
<h3 class=sectionHead>Directivity</h3>
<p>
   The directivity of a transducer refers to the variation of its response with the direction, which is defined by the polar angle
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>
   and the azimuthal angle
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
   </math>
   (see <a href=# data-img-src=${config.ASSETS_URL}/04/07/theory/figure-2.svg data-img-alt="Figure 2. Coordinate system.">Figure 2</a>). Directivity is a far-field quantity: it is evaluated at large distance from sound sources or with incident plane waves for microphones. The direction (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>
   is generally chosen as the direction for which the response of the transducer is maximum. For a microphone, it generally corresponds to the axis which is perpendicular to the membrane.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/07/theory/figure-2.svg alt="Figure 2. Coordinate system."><figcaption class=caption>Figure 2. Coordinate system.</figcaption></figure>
<h4 class=subsectionHead>Microphones</h4>
<p>For microphones, directivity patterns are calculated using
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>d</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>μ</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>ϕ</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>μ</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                              <mi>a</mi>
                              <mi>x</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>μ</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mi>a</mi>
                  <mi>x</mi>
               </mrow>
            </msub>
         </mrow>
      </msub>
   </math>
   is evaluated in the direction where the sensitivity is maximum. As many microphones generally have an axisymmetric geometry, their directivity patterns mainly depend on one angle (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>, when axis
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>z</mi>
   </math>
   is chosen to be perpendicular to the membrane). The quantity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mn>0</mn>
      <mi class=qopname>log</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>[</mo>
      <mi>d</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-close>]</mo>
   </math>
   is used to get plots in dB. The directivity factor of a microphone can be calculated using
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>Q</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>μ</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                              <mi>a</mi>
                              <mi>x</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mover accent=false class=mml-overline>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>S</mi>
                           </mrow>
                           <mrow>
                              <mi>μ</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=false class=mml-overline>
         <mrow>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   is the average angle sensitivity.
<h4 class=subsectionHead>Sound sources</h4>
<p>
   Consider a sound source radiating a sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant=bold-script>𝒫</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>, the mean acoustic intensity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=false class=mml-overline>
         <mrow>
            <mi>I</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   at distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   is then defined by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mover accent=false class=mml-overline>
               <mrow>
                  <mi>I</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>r</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi mathvariant=bold-script>𝒫</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   The division by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>4</mn>
      <mi>π</mi>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   indicates an average on the surface of a sphere of radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>. Then, the directivity factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
   </math>
   is given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>Q</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                        <mi>a</mi>
                        <mi>x</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>r</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mover accent=false class=mml-overline>
                     <mrow>
                        <mi>I</mi>
                        <mo class=MathClass-open>(</mo>
                        <mi>r</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                        <mi>a</mi>
                        <mi>x</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>r</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi mathvariant=bold-script>𝒫</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
         </mrow>
      </msub>
   </math>
   is the intensity that is evaluated in the direction where it is maximum. For an omnidirectional source, i.e. a source radiating with the same intensity in all directions,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>. A source radiating mainly in one direction will have a high
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
   </math>.<br class=newline>The directivity index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>D</mi>
      <mi>I</mi>
   </math>
   of a source expresses the directivity factor in dB
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>D</mi>
            <mi>I</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>Q</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(9)
</table>
<p>The directivity pattern is the representation of the transducer response as a function of the angle. It can be calculated for each frequency with
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>D</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>I</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>ϕ</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                        <mi>a</mi>
                        <mi>x</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>≃</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>ϕ</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                        <mi>a</mi>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is measured or calculated in the far-field of the source.<br class=newline>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>D</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is generally represented using polar plots (see <a href=# data-img-src=${config.ASSETS_URL}/04/07/theory/figure-3.svg data-img-alt="Figure 3. Polar plots of a bidirective source (top row) and a cardioid source (bottom row) for several frequencies.">Figure 3</a>) or 3D balloons (see <a href=# data-img-src=${config.ASSETS_URL}/04/07/theory/figure-4.png  data-img-alt="Figure 4. Balloon plot of a cardioid directivity.">Figure 4</a>) for each frequency. Waterfall (see <a href=# data-img-src=${config.ASSETS_URL}/04/07/theory/figure-5.png data-img-alt="Figure 5. Waterfall plot of the theorerical directivity of a planar piston.">Figure 5</a>) and intensity maps can also be used to plot the whole frequency range in the same figure. When the directivity is plotted using a decibel scale, the quantity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>0</mn>
      <mi class=qopname>log</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>[</mo>
      <mi>D</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-close>]</mo>
   </math>
   is computed to draw the graphs.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/07/theory/figure-3.svg alt="Figure 3. Polar plots of a bidirective source (top row) and a cardioid source (bottom row) for several frequencies."><figcaption class=caption>Figure 3. Polar plots of a bidirective source (top row) and a cardioid source (bottom row) for several frequencies.</figcaption></figure><figure class=figure><p><img src=${config.ASSETS_URL}/04/07/theory/figure-4.png alt="Figure 4. Balloon plot of a cardioid directivity."><figcaption class=caption>Figure 4. Balloon plot of a cardioid directivity.</figcaption></figure><figure class=figure><p><img src=${config.ASSETS_URL}/04/07/theory/figure-5.png alt="Figure 5. Waterfall plot of the theorerical directivity of a planar piston."><figcaption class=caption>Figure 5. Waterfall plot of the theorerical directivity of a planar piston.</figcaption></figure>
<h3 class=sectionHead>Efficiency</h3>
<p>The efficiency of transducers is calculated from the ratio of the output power to the input power:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>η</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi mathvariant=bold-script>𝒫</mi>
                     </mrow>
                     <mrow>
                        <mi>o</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi mathvariant=bold-script>𝒫</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(11)
</table>
<p>It can be noted that in the case of loudspeakers, the efficiency is usually very low ( between 0.1% and 3%), and that most of the input power is converted to heat. One possibility to increase their efficiency is to equip them with a horn, which is generally done on public address systems that require a high sound level to be radiated over the large area where the audience is located.
<h3 class=sectionHead>Electrical impedance</h3>
<p>
   Transducers are never used alone, and are connected to other elements such as amplifiers, filters, generation/acquisition systems, etc. It is therefore important to know the electrical impedances of the various systems as their matching can have a significant effect on the overall system response. For example, the fine tuning of crossover filters in a loudspeaker system requires the knowledge of the impedances of the loudspeakers. It is also important to check that the impedances are compatible and that there is no risk of damage to any component, <em><span class=cmti-10>i.e.</span></em> speakers with a nominal impedance of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>4</mn>
   </math>

   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo>Ω</mo>
   </math>
   should be connected to amplifiers that can handle low loads.
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, the linear parameters of the transducers were presented. The lists of parameters described here is of course not exhaustive, manufacturers can also provide other relevant quantities such as electrical impedance, resonance frequency, theoretical parameters (for example, Thiele & Small parameters of electrodynamic speakers).
<h3 class=likesectionHead>Glossary</h3>
<div class=supertabular>
    <dl>
        <dt>RMS
        </dt>
        <dd>Root Mean Square</dd>
        <dt>SPL</dt>
        <dd>Sound Pressure Level</dd>
    </dl>
</div>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-Beranek2012 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Leo Beranek and Tim Mellow. <span class=cmti-10>Acoustics: sound fields and transducers</span>. Academic Pres, 2012. <span class=cmcsc-10><span class=small-caps>i</span><span class=small-caps>s</span><span class=small-caps>b</span><span class=small-caps>n</span></span>: 9780123914866.
   <dt id=X0-Kleiner2013 class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Mendel Kleiner. <span class=cmti-10>Electroacoustics</span>. CRC Press, 2013. <span class=cmcsc-10><span class=small-caps>i</span><span class=small-caps>s</span><span class=small-caps>b</span><span class=small-caps>n</span></span>: 9780429073519.
   <dt id=X0-Rossi1988 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Mario Rossi. <span class=cmti-10>Acoustics and Electroacoustics</span>. Artech House, 1988. <span class=cmcsc-10><span class=small-caps>i</span><span class=small-caps>s</span><span class=small-caps>b</span><span class=small-caps>n</span></span>: 978-0890062555.
</dl>`;

export default theory;
